import { debounce, uniqBy } from 'lodash';
import { Pin } from 'lucide-react';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ChainlitContext,
  IThread,
  isThreadPinned,
  threadActivityTime
} from '@chainlit/react-client';

import { Skeleton } from '@/components/ui/skeleton';

import { Translator } from '../i18n';

/**
 * Shared pieces of the two history searches: the Cmd+K dialog and the box at
 * the top of the sidebar. The server does the matching (Arabic-aware, every
 * word, titles + questions + answers) and says WHY each result matched:
 * `titleHighlights`, `matchKind`, `snippet`, `snippetHighlights`.
 */

export const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_PAGE = 25;

type Range = [number, number] | number[];

/** `text` with the given [start, end) ranges wrapped in <mark>. */
export function HighlightedText({
  text,
  ranges,
  className
}: {
  text: string;
  ranges?: Range[];
  className?: string;
}) {
  if (!ranges || ranges.length === 0) {
    return (
      <span className={className} dir="auto">
        {text}
      </span>
    );
  }
  const parts: JSX.Element[] = [];
  let cursor = 0;
  [...ranges]
    .map((r) => [Math.max(0, r[0]), Math.min(text.length, r[1])])
    .filter(([s, e]) => e > s)
    .sort((a, b) => a[0] - b[0])
    .forEach(([s, e], i) => {
      if (s < cursor) return;
      if (s > cursor) parts.push(<span key={`t${i}`}>{text.slice(cursor, s)}</span>);
      parts.push(
        <mark
          key={`m${i}`}
          className="rounded-sm bg-yellow-200/70 px-0.5 text-inherit dark:bg-yellow-500/30"
        >
          {text.slice(s, e)}
        </mark>
      );
      cursor = e;
    });
  if (cursor < text.length) parts.push(<span key="tail">{text.slice(cursor)}</span>);
  return (
    <span className={className} dir="auto">
      {parts}
    </span>
  );
}

/** "5 min ago", "Yesterday", "3 Sep" — in the interface language. */
export function useThreadTime() {
  const { i18n } = useTranslation();
  return useMemo(() => {
    const lang = i18n.language || 'en-US';
    let rtf: Intl.RelativeTimeFormat | null = null;
    try {
      rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
    } catch {
      rtf = null;
    }
    return (thread: IThread) => {
      const then = threadActivityTime(thread);
      if (!then) return '';
      const minutes = Math.round((Date.now() - then) / 60000);
      if (rtf && minutes < 60) return rtf.format(-Math.max(1, minutes), 'minute');
      if (rtf && minutes < 24 * 60) return rtf.format(-Math.round(minutes / 60), 'hour');
      if (rtf && minutes < 7 * 24 * 60) return rtf.format(-Math.round(minutes / 1440), 'day');
      try {
        return new Date(then).toLocaleDateString(lang, { day: 'numeric', month: 'short' });
      } catch {
        return new Date(then).toLocaleDateString();
      }
    };
  }, [i18n.language]);
}

/** One search result: highlighted title, where it matched, and an excerpt. */
export function ThreadSearchResult({ thread }: { thread: IThread }) {
  const formatTime = useThreadTime();
  const snippet = thread.snippet;
  const kind = thread.matchKind;
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
      <div className="flex min-w-0 items-start gap-2">
        {isThreadPinned(thread) ? (
          <Pin className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
        ) : null}
        {/* Search results wrap to two lines: the point of a result is to
            be recognised, and a cut title is the part that recognises it. */}
        <HighlightedText
          className="line-clamp-2 break-words font-medium"
          text={thread.name || ''}
          ranges={thread.titleHighlights}
        />
        <span className="ml-auto shrink-0 self-start pt-0.5 text-xs text-muted-foreground">
          {formatTime(thread)}
        </span>
      </div>
      {snippet ? (
        <div
          className="flex min-w-0 items-baseline gap-1.5 text-xs text-muted-foreground"
        >
          {kind === 'question' || kind === 'answer' ? (
            <span className="shrink-0 rounded bg-muted px-1 py-px text-[10px] uppercase tracking-wide">
              <Translator
                path={
                  kind === 'question'
                    ? 'threadHistory.search.matchQuestion'
                    : 'threadHistory.search.matchAnswer'
                }
              />
            </span>
          ) : null}
          <HighlightedText
            className="line-clamp-2 break-words"
            text={snippet}
            ranges={thread.snippetHighlights}
          />
        </div>
      ) : null}
    </div>
  );
}

/** Placeholder rows while a list loads. */
export function ThreadRowsSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-2 px-2 py-2" aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton
          key={i}
          className="h-5"
          style={{ width: `${55 + ((i * 37) % 40)}%` }}
        />
      ))}
    </div>
  );
}

/**
 * Server-side search with debounce, paging and a guard against an older
 * response landing after a newer one.
 */
export function useThreadSearch(query: string, pageSize = SEARCH_PAGE) {
  const apiClient = useContext(ChainlitContext);
  const [results, setResults] = useState<IThread[]>([]);
  const [cursor, setCursor] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string>();
  const latest = useRef('');

  const run = useMemo(
    () =>
      debounce(async (q: string) => {
        latest.current = q;
        if (!q) {
          setResults([]);
          setHasMore(false);
          setCursor(undefined);
          setLoading(false);
          return;
        }
        try {
          const { data, pageInfo } = await apiClient.listThreads(
            { first: pageSize },
            { search: q }
          );
          if (latest.current !== q) return;
          setResults(data || []);
          setHasMore(!!pageInfo?.hasNextPage);
          setCursor(pageInfo?.endCursor || undefined);
          setError(undefined);
        } catch (err) {
          if (latest.current === q) {
            setError(err instanceof Error ? err.message : String(err));
          }
        } finally {
          if (latest.current === q) setLoading(false);
        }
      }, SEARCH_DEBOUNCE_MS),
    [apiClient, pageSize]
  );

  useEffect(() => {
    const q = query.trim();
    setLoading(!!q);
    run(q);
    return () => run.cancel();
  }, [query, run]);

  const loadMore = async () => {
    const q = latest.current;
    if (!q || !hasMore || !cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const { data, pageInfo } = await apiClient.listThreads(
        { first: pageSize, cursor },
        { search: q }
      );
      if (latest.current !== q) return;
      setResults((prev) => uniqBy(prev.concat(data || []), 'id'));
      setHasMore(!!pageInfo?.hasNextPage);
      setCursor(pageInfo?.endCursor || undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoadingMore(false);
    }
  };

  return { results, loading, loadingMore, hasMore, loadMore, error };
}
